import { Platform } from "quasar";
import { useRouter } from "vue-router";
import { useCommonStore } from "src/stores/common-store";
import { useNotificationDetails } from "src/stores/notification-store";
import { usePersistedStore } from "src/stores/persisted-store";

/**
 * Single source of truth for push-notification redirection, shared by the
 * native (Cordova) flow in App.vue and the web/PWA flow in
 * HandlePushNotificationPWA.vue.
 *
 * Key robustness rule: the payload lives in `persistedStore.pendingNotification`
 * (persisted to localStorage), NOT in a volatile in-memory variable or the URL
 * query. It is cleared ONLY after the target navigation has actually resolved,
 * so a full reload mid-startup simply retries the same redirect instead of
 * dropping it.
 */
export function useNotificationRedirection() {
  const router = useRouter();
  const commonStore = useCommonStore();
  const notificationStore = useNotificationDetails();
  const persistedStore = usePersistedStore();

  // Per-instance in-flight guard. The mobile timer reads this to avoid firing
  // a second redirect while one is already running.
  let loadingApi = false;
  const isLoading = () => loadingApi;

  // Read the durable payload. Empty object means "nothing pending".
  const getPayload = () => persistedStore.pendingNotification || {};

  // Store a fresh payload durably (used on arrival / on page mount).
  const setPayload = (payload) => {
    persistedStore.pendingNotification =
      payload && Object.keys(payload).length ? { ...payload } : null;
  };

  const hasPending = () => Object.keys(getPayload()).length > 0;

  // Clear ONLY after a successful navigation so reloads can retry safely.
  const clearPending = () => {
    loadingApi = false;
    commonStore.setLoader(false);
    persistedStore.pendingNotification = null;
  };

  // Navigate then clear the pending payload once the push settles. If the push
  // rejects we still clear to avoid an infinite redirect loop.
  const go = (location) => {
    Promise.resolve(router.push(location))
      .catch(() => {})
      .finally(() => clearPending());
  };

  function sendToHome() {
    if (Platform?.is?.mobile) {
      go({ name: "Home" });
    } else {
      go({ name: "Home", query: { openNotification: true } });
    }
  }

  function seeCoupon(notificationObj) {
    let objtmp = null;
    if (notificationObj?.full_description) {
      try {
        objtmp = JSON.parse(notificationObj.full_description);
      } catch (e) {
        objtmp = null;
      }
    }

    if (
      objtmp?.type == "manufacturer" &&
      objtmp?.manufacturer_account_id?.length > 0
    ) {
      // Select the store context first (web already did this; mobile now matches).
      if (notificationObj?.account_id) {
        commonStore.selectStoreForGlobal({ id: notificationObj.account_id });
      }
      go({
        name: "MyMFGDetail",
        params: { manufacturerid: objtmp.manufacturer_account_id, from: "Mfg" },
        query: { tab: "Coupons" },
      });
    } else if (notificationObj?.account_id?.length > 0) {
      commonStore.selectStoreForGlobal({ id: notificationObj.account_id });
      if (Platform?.is?.mobile) {
        go({ name: "MyCoupons", params: { storeid: notificationObj.account_id } });
      } else {
        go({
          name: "StoreDetail",
          params: { storeid: notificationObj.account_id },
          query: { tab: "Coupons" },
        });
      }
    } else {
      sendToHome();
    }
  }

  function redirectToTokenEarned(notificationObj) {
    let objtmp = null;
    if (notificationObj?.full_description) {
      try {
        objtmp = JSON.parse(notificationObj.full_description);
      } catch (e) {
        objtmp = null;
      }
    }

    const handlePointsBreakdownDetail = (storeId, type) => {
      if (type == "store") {
        commonStore.selectStoreForGlobal({ id: storeId });
      }
      go({ name: "PointsBreakdownDetail", params: { storeid: storeId, type } });
    };

    const handleMyProfile = (storeId) => {
      if (storeId) {
        commonStore.selectStoreForGlobal({ id: storeId });
      }
      go({
        name: "MyProfile",
        query: { tab: "earning", fromnotification: true },
      });
    };

    if (Platform?.is?.mobile) {
      if (
        objtmp?.type === "manufacturer" &&
        objtmp?.manufacturer_account_id?.length > 0
      ) {
        handlePointsBreakdownDetail(objtmp.manufacturer_account_id, "mfg");
      } else if (notificationObj?.account_id?.length > 0) {
        handlePointsBreakdownDetail(notificationObj.account_id, "store");
      } else {
        sendToHome();
      }
    } else {
      if (
        objtmp?.type === "manufacturer" &&
        objtmp?.manufacturer_account_id?.length > 0
      ) {
        if (notificationObj?.account_id) {
          commonStore.selectStoreForGlobal({ id: notificationObj.account_id });
        }
        go({
          name: "MyMFGDetail",
          params: { manufacturerid: objtmp.manufacturer_account_id, from: "Mfg" },
        });
      } else {
        handleMyProfile(notificationObj?.account_id);
      }
    }
  }

  function notificationRedirection() {
    const notiPayload = getPayload();
    const type = notiPayload?.type;

    // Nothing actionable — bail without touching navigation.
    if (!type) {
      clearPending();
      return;
    }

    commonStore.setLoader(true);
    loadingApi = true;

    if (type == "Customer Message") {
      setTimeout(() => {
        if (Platform.is.mobile && notiPayload.group_id) {
          go({ name: "Chat", params: { msgId: notiPayload.group_id } });
        } else {
          go({ name: "Messages" });
        }
      }, 2000);
    } else if (
      (type == "New Campaign" ||
        type == "Campaign Completion" ||
        type == "Campaign Ending" ||
        type == "Special Offers Available!") &&
      notiPayload.id
    ) {
      setTimeout(() => {
        const store_id =
          notiPayload?.account_id || notiPayload?.store_id || notiPayload?.id || null;
        if (store_id) {
          if (type?.toLowerCase() == "special offers available!") {
            go({ name: "StoreDetail", params: { storeid: store_id } });
          } else {
            commonStore.selectStoreForGlobal({ id: store_id });
            go({
              name: "DealDetail",
              params: { dealid: notiPayload.id, type: "store" },
            });
          }
        } else {
          clearPending();
        }
      }, 2000);
    } else if (type == "Customer Receipt" && notiPayload.id && notiPayload.account_id) {
      commonStore.selectStoreForGlobal({ id: notiPayload.account_id });
      setTimeout(() => {
        go({ name: "ReceiptsDetail", params: { id: notiPayload.id } });
      }, 2000);
    } else if (type == "Friend Request" || type == "Team Request") {
      setTimeout(() => {
        go({ name: "AllRequests" });
      }, 2000);
    } else if (type == "Gift Received" && notiPayload?.account_id) {
      commonStore.selectStoreForGlobal({ id: notiPayload.account_id });
      setTimeout(() => {
        if (Platform?.is?.mobile && notiPayload.id) {
          go({
            name: "GiftsDetail",
            params: { giftId: notiPayload.id },
            query: { category: "store", type: "received" },
          });
        } else {
          go({ name: "Gifts", query: { category: "store", type: "" } });
        }
      }, 2000);
    } else if (type == "Multiple New Campaign" && notiPayload.id) {
      commonStore.selectStoreForGlobal({ id: notiPayload.id });
      setTimeout(() => {
        if (Platform?.is?.mobile) {
          go({ name: "MyDeal", params: { storeid: notiPayload.id } });
        } else {
          go({ name: "StoreDetail", params: { storeid: notiPayload.id } });
        }
      }, 2000);
    } else if (type == "Tokens Earned") {
      setTimeout(() => {
        redirectToTokenEarned(notiPayload);
      }, 2000);
    } else if (type == "Coupons Earned") {
      setTimeout(() => {
        seeCoupon(notiPayload);
      }, 2000);
    } else if (type == "Raffle Points Earned" && notiPayload.id && notiPayload.account_id) {
      commonStore.selectStoreForGlobal({ id: notiPayload.account_id });
      setTimeout(() => {
        go({
          name: "DealDetail",
          params: { dealid: notiPayload.id, type: "store" },
        });
      }, 2000);
    } else if (
      (type == "Coupon Expiration" || type == "Custom Coupon") &&
      notiPayload?.id
    ) {
      commonStore.selectStoreForGlobal({ id: notiPayload.id });
      setTimeout(() => {
        if (Platform?.is?.mobile) {
          go({ name: "MyCoupons", params: { storeid: notiPayload.id } });
        } else {
          go({
            name: "StoreDetail",
            params: { storeid: notiPayload.id },
            query: { tab: "Coupons" },
          });
        }
      }, 2000);
    } else if (type?.toLowerCase() == "pplus" && notiPayload?.id) {
      commonStore.selectStoreForGlobal({ id: notiPayload.id });
      setTimeout(() => {
        notificationStore.trackNotificationForTypePPlus(
          notiPayload?.offer_type,
          notiPayload?.offer_id
        );
        if (Platform?.is?.mobile) {
          go({ name: "MyDeal", params: { storeid: notiPayload.id } });
        } else {
          go({ name: "StoreDetail", params: { storeid: notiPayload.id } });
        }
      }, 2000);
    } else {
      // Unknown / non-deep-link type: open the notifications surface.
      setTimeout(() => {
        if (Platform.is.mobile) {
          go({ name: "Notifications" });
        } else {
          go({ name: "Home", query: { openNotification: true } });
        }
      }, 2000);
    }
  }

  return {
    isLoading,
    getPayload,
    setPayload,
    hasPending,
    clearPending,
    notificationRedirection,
  };
}
